import React from 'react';
import {useTranslation} from 'react-i18next';
import config from '../../service/config';
import {ControlButton, TscButtonType, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import Constants from '../../constants/Constants';
import CssUtil from '../../util/CssUtil';
import styled from 'styled-components';
import CamtasiaLogoSvg from '../../static/svg/CamtasiaWebLogo.svg';
import HomeIconSvg from '../../static/svg/HomeIcon.svg';
import cssConstants from '../../constants/cssConstants';

const NoAccessBackGround = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   background: ${cssConstants.noAccessPageBackground};
   min-height: 100svh;
   padding: 1rem;
`;

const backgroundAlpha = 0.85;
const NoAccessCard = styled.div`
   display: flex;
   flex-wrap: wrap;
   flex-direction: column;
   justify-content: center;
   align-content: center;
   background: ${CssUtil.toRgba(TscStyles.color.ui.dawn.light, backgroundAlpha)};
   box-shadow: ${cssConstants.noAccessCardBoxShadow};
   border-radius: ${TscStyles.border.radius.xxl};
   width: 33.75rem;
   height: fit-content;
   padding: 1.5rem;
`;

const NoAccessCardField = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   margin-bottom: 2.75rem;
`;

const NoAccessCardLastField = styled(NoAccessCardField)`
   margin-bottom: 0;
`;

const LogoField = styled.span`
    display: flex;
    flex-direction: row;
`;

const Logo = styled.span`
   display:flex;

   svg {
      height: 2rem;
      width: 2rem;
   }
`;

const LogoText = styled.span`
   font-size: 1.625rem;
   font-weight: 320;
   letter-spacing: -0.05rem;
   line-height: 1.95rem;
   text-align: center;
   margin-left: 0.5rem;
`;

const PrimaryMessageField = styled.span`
   font-size: 1.5rem;
   font-weight: 500;
   line-height: 1.75rem;
   margin-bottom: 1rem;
   text-align: center;
   color: ${TscStyles.color.text.mediumDark};
`;

const UserEmailField = styled.span`
   align-content: center;
   border-radius: ${TscStyles.border.radius.md};
   background-color: #EFF0F1;
   padding: 0 0.75rem;
   height: 2rem;
   font-size: 0.875rem;
   font-weight: 500;
   line-height: 1rem;
`;

const SignedInMessageField = styled.span`
   font-size: 1rem;
   font-weight: 500;
   line-height: 1.25rem;
   margin-bottom: 0.5rem;
   color: ${TscStyles.color.text.mediumDark};
`;

const GoHomeButton = styled(ControlButton)`
   background-color: ${cssConstants.noAccessGoHomeButtonBg};
   box-shadow: ${cssConstants.noAccessGoHomeButtonShadow};
   border-radius: ${TscStyles.border.radius.md};

   :hover {
      background-color: ${cssConstants.noAccessGoHomeButtonBgHover};
      box-shadow: ${cssConstants.noAccessGoHomeButtonShadowHover};
   }

   :active {
      background-color: ${cssConstants.noAccessGoHomeButtonBgActive};
      box-shadow: ${cssConstants.noAccessGoHomeButtonShadowActive};
   }
`;

const SignOutButton = styled(ControlButton)`
   border-radius: ${TscStyles.border.radius.md};
   background-color: ${cssConstants.noAccessSignOutButtonBg};
   box-shadow: ${cssConstants.noAccessSignOutButtonShadow};
   border: 1px solid ${cssConstants.noAccessSignOutButtonBorder};
   margin-left: 0.5rem;

   :hover {
      background-color: ${cssConstants.noAccessSignOutButtonBgHover};
      box-shadow: ${cssConstants.noAccessSignOutButtonShadowHover};
   }
      
   :active {
      background-color: ${cssConstants.noAccessSignOutButtonBgActive};
      box-shadow: ${cssConstants.noAccessSignOutButtonShadowActive};
   }
`;

const UserAccountField = styled.span`
   display: flex;
   color: ${TscStyles.color.text.mediumDark};
`;

export const NoAccessBase: React.FC = () => {
   const {t} = useTranslation();

   return (
      <NoAccessBackGround>
         <NoAccessCard data-test-id="no-access-card">
            <NoAccessCardField>
               <LogoField>
                  <Logo>
                     <CamtasiaLogoSvg />
                  </Logo>
                  <LogoText>
                     {t('Camtasia')}
                  </LogoText>
               </LogoField>
            </NoAccessCardField>

            <NoAccessCardField>
               <PrimaryMessageField>
                  {t('noAccess.dontHaveAccess')}
               </PrimaryMessageField>
               <GoHomeButton
                  buttonType={TscButtonType.primary}
                  testId="no-access-go-home-button"
                  glyph={<HomeIconSvg />}
                  href={Constants.navigation.camtasiaWeb}
                  label={t('noAccess.goHome')}
               />
            </NoAccessCardField>

            <NoAccessCardLastField>
               <SignedInMessageField>
                  {t('noAccess.signedInAs')}
               </SignedInMessageField>
               <UserAccountField>
                  <UserEmailField>
                     {config.user.EmailAddress}
                  </UserEmailField>
                  <SignOutButton
                     buttonType={TscButtonType.secondary}
                     testId="no-access-sign-out-button"
                     href={config.environmentData.SignOutUrl}
                     label={t('SignOut')}
                  />
               </UserAccountField>
            </NoAccessCardLastField>
         </NoAccessCard>
      </NoAccessBackGround>
   );
};

export default (NoAccessBase);